import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/Badge";

interface SectionCardProps {
  title?: string;
  icon?: LucideIcon;
  columns?: 1 | 2 | 3 | 4;
  children: ReactNode;
  className?: string;
  status?: "complete" | "incomplete" | "empty";
  actions?: ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /**
   * "compact" resserre le chrome de la carte (padding, marges d'en-tête).
   *
   * Motivation (chantier mise en page 2026-08-08) : sur l'onglet « Le bien »,
   * huit blocs empilés coûtaient 103 px de chrome CHACUN — en-tête, séparateur
   * et p-6 — soit 824 px pour 1289 px de champs réellement utiles. Le cadre
   * reste (il porte la séparation demandée), mais il cesse de peser plus que
   * son contenu.
   */
  density?: "normal" | "compact";
}

const gridClasses = {
  1: "grid grid-cols-1 gap-y-6",
  2: "grid grid-cols-2 gap-x-8 gap-y-6",
  3: "grid grid-cols-3 gap-x-8 gap-y-6",
  4: "grid grid-cols-4 gap-x-8 gap-y-6",
};

export function SectionCard({
  title,
  icon: Icon,
  columns = 2,
  children,
  className,
  status,
  actions,
  collapsible = false,
  defaultCollapsed = false,
  density = "normal",
}: SectionCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const compact = density === "compact";

  const header = title && (
    <>
      <div
        className={cn(
          "flex items-center justify-between",
          collapsed ? "mb-0" : compact ? "mb-2.5" : "mb-4",
          collapsible && "cursor-pointer select-none group"
        )}
        onClick={() => collapsible && setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          {Icon && (
            <Icon
              size={16}
              className={cn(
                "transition-colors",
                collapsible ? "text-text-muted group-hover:text-text-secondary" : "text-text-secondary"
              )}
            />
          )}
          <h3 className={cn(
            "text-xs font-semibold tracking-widest uppercase transition-colors",
            collapsible ? "text-text-muted group-hover:text-text-secondary" : "text-text-secondary"
          )}>
            {title}
          </h3>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {status === "complete" && <Badge variant="publie">Complet</Badge>}
          {status === "incomplete" && <Badge variant="a_publier">Incomplet</Badge>}
          {status === "empty" && <Badge variant="brouillon">Vide</Badge>}
          {actions}
          {collapsible && (
            <ChevronDown
              size={15}
              className={cn(
                "text-text-muted transition-transform duration-200",
                collapsed && "-rotate-90"
              )}
            />
          )}
        </div>
      </div>
      {!collapsed && <div className={cn("border-b border-border", compact ? "mb-4" : "mb-5")} />}
    </>
  );

  return (
    <div
      data-section-card
      className={cn(
        "bg-surface border border-border rounded-lg shadow-sm",
        compact ? "px-5 py-4" : "p-6",
        !collapsed && "h-full",
        className
      )}
    >
      {header}
      {!collapsed && <div data-section-grid className={gridClasses[columns]}>{children}</div>}
    </div>
  );
}
